import type { ComponentProps } from "react";
import { EActivityStatus } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/ActivityStatus.enum";
import { Tag } from "@timeedit/ui-components";

type TStatusTagProps = ComponentProps<typeof Tag.StatusTag>;

/**
 * Status tag state and fallback English label per activity status.
 */
const activityStatuses = {
    [EActivityStatus.CREATED]: { state: "Default", label: "Created" },
    [EActivityStatus.NOT_SCHEDULED]: { state: "Default", label: "Not scheduled" },
    [EActivityStatus.IN_REVIEW]: { state: "Warning", label: "In review" },
    [EActivityStatus.SUBMITTED]: { state: "New", label: "Submitted" },
    [EActivityStatus.DECLINED_IN_REVIEW]: {
        state: "Error",
        label: "Do not schedule",
    },
    [EActivityStatus.SEND_TO_REVIEW_FAILED]: {
        state: "Error",
        label: "Send to review failed",
    },
    [EActivityStatus.SCHEDULED]: { state: "Success", label: "Scheduled" },
    [EActivityStatus.SCHEDULING_IN_PROGRESS]: {
        state: "Processing",
        label: "In progress",
    },
    [EActivityStatus.QUEUED]: { state: "Warning", label: "Queued" },
    [EActivityStatus.FAILED]: { state: "Error", label: "Failed" },
    [EActivityStatus.VALIDATION_ERROR]: {
        state: "Error",
        label: "Validation error",
    },
    [EActivityStatus.NO_AVAILABILITY]: {
        state: "Error",
        label: "No availability",
    },
    [EActivityStatus.MODIFIED]: { state: "Warning", label: "Modified" },
    [EActivityStatus.DELETED]: { state: "Default", label: "Deleted" },
    [EActivityStatus.INACTIVE]: { state: "Default", label: "Inactive" },
    [EActivityStatus.GROUPED_MULTIPLE]: {
        state: "Warning",
        label: "Multiple statuses",
    },
} satisfies Record<EActivityStatus, { state: TStatusTagProps["state"]; label: string }>;

type TActivityStatusTagProps = Omit<TStatusTagProps, "state"> & {
    status: EActivityStatus;
    label?: string;
};

export const ActivityStatusTag = ({
    status,
    label,
    children,
    ...tagProps
}: TActivityStatusTagProps) => {
    const known: (typeof activityStatuses)[EActivityStatus] | undefined = activityStatuses[status];
    return (
        <Tag.StatusTag state={known?.state ?? "Default"} variant="outlined" {...tagProps}>
            {label ?? known?.label ?? status}
            {children}
        </Tag.StatusTag>
    );
};
